import { useMounted, useXhr } from '@fullstory/react-hooks';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { AnyRequestLifecycleAction, RequestLifecycleActionReturn, RequestState } from '../types';
import { getCommonRequest, processRequestAction } from '../request';
import { createRequestReducer } from '../middleware/request/reducers';
import { buildExecuteRequestOptions, cache, getCacheKey, requestMap } from '@fullstory/requests';
import { useQueryClient } from '@tanstack/react-query';

let counter = 0;

enum requestType {
  request = 'request',
  success = 'success',
  failure = 'failure',
}

const defaultState: RequestState<any> = {
  lastRequested: -1,
  lastResponded: -1,
  loading: false,
  error: false,
  data: null,
  safeMessage: null,
};

/**
 * useFetch is intended for the "fire and forget" usecase, when _no_ state is needed to be tracked, or if you are managing
 * state, you would like to handle it yourself.
 *
 * As an example of fire and forget:
 * ```js
 * const fetch = useFetch();
 * const notifications = useNotifications();
 *
 * return <MyComponent
 *    onSubmit={() => {
 *      fetch(DetailRequests.updateDetail(orgId))
 *        .then(() => {
 *          notifications.success('Successfully updated detail');
 *        }).catch(() => {
 *          notifications.error('Failed to update detail');
 *        })
 *    }}
 *  />
 * ```
 * If you are managing state yourself, keep in mind that one of the main problems that useXhrRequest solves
 * for you is handling out of order responses. If you're firing the same request in quick succession, it's recommended
 * to use that hook instead.
 */
export function useFetch() {
  const xhr = useXhr();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const fetch = useCallback(
    function <R extends AnyRequestLifecycleAction>(request: R) {
      return processRequestAction<RequestLifecycleActionReturn<R>>(
        request,
        xhr,
        dispatch,
        buildExecuteRequestOptions(queryClient),
      );
    },
    [dispatch, queryClient, xhr],
  );

  return fetch;
}

/**
 * useXhrRequest is intended to be used declaratively, most typically for fetch endpoints where you are requesting data,
 * and want to store it in local component state. It gives you data back in the same shape as if you were using
 * `createRequestReducer`, but allows you to skip storing the data in Redux itself.
 *
 * WARNING: Make sure that the request that you provide here is memoized, otherwise the hook will cycle.
 * ```ts
 * const myRequest = useMemo(() => DetailRequests.fetchDetail(orgId), [orgId]);
 * const { data, isReady } = useXhrRequest(myRequest);
 * ```
 */
export function useXhrRequest<R extends AnyRequestLifecycleAction>(
  request: R | null,
): RequestState<RequestLifecycleActionReturn<typeof request>> & {
  isReady: boolean;
  invalidateCache: () => void;
  refetch: () => void;
  cancel: () => void;
} {
  type M = RequestLifecycleActionReturn<typeof request>;
  const xhr = useXhr();
  const isMounted = useMounted();
  const fetch = useFetch();

  const requestReducer = useMemo(
    () => createRequestReducer<M>()(requestType.request, requestType.success, requestType.failure),
    [],
  );
  const [requestState, dispatchForm] = useReducer(requestReducer, defaultState);

  const processFetch = useCallback(() => {
    if (!request) return;
    const sequenceNumber = counter++;
    dispatchForm({
      ...request,
      type: requestType.request,
      sequenceNumber,
    });
    fetch(request)
      .then(data => {
        if (!isMounted()) return;
        dispatchForm({
          ...request,
          type: requestType.success,
          sequenceNumber,
          data,
        });
      })
      .catch((error: any) => {
        if (!isMounted()) return;
        dispatchForm({
          ...request,
          type: requestType.failure,
          sequenceNumber,
          error,
        });
      });
  }, [isMounted, request, fetch]);

  const cancel = useCallback(() => {
    if (!request) return;
    const inFlight = requestMap[request.id];
    if (inFlight) {
      inFlight.request.Abort();
      delete requestMap[request.id];
      cache.del(inFlight.cacheKey);
    }
  }, [request]);

  const invalidateCache = useCallback(() => {
    if (!request) return;
    const commonRequest = getCommonRequest(request, xhr);
    cache.del(getCacheKey(commonRequest));
  }, [request, xhr]);

  const refetch = useCallback(() => {
    cancel();
    invalidateCache();
    processFetch();
  }, [cancel, invalidateCache, processFetch]);

  useEffect(() => {
    processFetch();
    return () => {
      cancel();
    };
  }, [cancel, processFetch]);

  return {
    ...requestState,
    isReady: !requestState.loading && !requestState.error && requestState.lastRequested > -1,
    invalidateCache,
    refetch,
    cancel,
  };
}
